/**
 * MC Status Grid -- Auto-refresh polling module with DOM diffing and animations.
 *
 * Fetches server data every 30 seconds and smoothly updates cards without
 * page reload. Handles tab visibility, card reordering, player chip
 * transitions, and relative timestamp updates.
 */

import { McConsole } from './mc/console';
import { initAnalytics } from './mc/analytics';

interface PlayerData {
    name: string;
    uuid: string;
}

interface ServerData {
    key: string;
    name: string;
    isOnline: boolean;
    onlinePlayers: number | null;
    maxPlayers: number | null;
    players: PlayerData[];
    version: string | null;
    motdHtml: string | null;
    favicon: string | null;
    latency: number | null;
    lastSeenAt: string | null;
}

interface DetailServerData extends ServerData {
    address: string;
    bluemapUrl: string | null;
}

const POLL_INTERVAL = 30_000;

export default class {
    private static grid: HTMLElement | null = null;
    private static statusUrl: string = '';
    private static pollTimer: ReturnType<typeof setTimeout> | null = null;

    static init(): void {
        // Always attempt analytics init (guard inside checks for #mc-analytics-chart)
        initAnalytics();

        this.grid = document.querySelector('.mc-grid');
        if (this.grid) {
            this.statusUrl = this.grid.dataset.statusUrl ?? '';
            if (!this.statusUrl) return;

            // Initial relative time render + start polling
            updateRelativeTimes();
            this.schedulePoll();

            // Visibility API: pause when hidden, immediate fetch on return
            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    this.clearPoll();
                } else {
                    this.poll();
                }
            });
            return;
        }

        const detail = document.querySelector<HTMLElement>('.mc-detail');
        if (detail) {
            initDetail(detail);
            return;
        }
    }

    private static schedulePoll(): void {
        this.pollTimer = setTimeout(() => this.poll(), POLL_INTERVAL);
    }

    private static clearPoll(): void {
        if (this.pollTimer !== null) {
            clearTimeout(this.pollTimer);
            this.pollTimer = null;
        }
    }

    private static async poll(): Promise<void> {
        this.clearPoll();

        try {
            const resp = await fetch(this.statusUrl, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            if (!resp.ok) {
                console.error(`[mc] Poll failed: ${resp.status}`);
            } else {
                const data: ServerData[] = await resp.json();
                updateGrid(data);
            }
        } catch (err) {
            console.error('[mc] Poll error:', err);
        }

        this.schedulePoll();
    }
}

// ---------------------------------------------------------------------------
// Grid update (DOM diffing)
// ---------------------------------------------------------------------------

function updateGrid(servers: ServerData[]): void {
    for (const server of servers) {
        const col = document.querySelector<HTMLElement>(
            `.mc-card-col[data-server-key="${server.key}"]`,
        );
        if (!col) continue;

        const card = col.querySelector<HTMLElement>('.mc-card');
        if (!card) continue;

        // Status class
        card.classList.toggle('mc-card--online', server.isOnline);
        card.classList.toggle('mc-card--offline', !server.isOnline);

        // Server name
        const nameEl = card.querySelector<HTMLElement>('h6');
        if (nameEl) nameEl.textContent = server.name;

        // Latency badge
        updateLatency(card, server);

        // Version
        const versionEl = card.querySelector<HTMLElement>('.mc-card-version');
        if (versionEl) versionEl.textContent = server.version ?? 'Unknown';

        // MOTD
        const motdEl = card.querySelector<HTMLElement>('.mc-motd');
        if (motdEl) {
            if (server.motdHtml) {
                motdEl.innerHTML = server.motdHtml;
                motdEl.style.display = '';
            } else {
                motdEl.innerHTML = '';
                motdEl.style.display = 'none';
            }
        }

        // Favicon
        const iconImg = card.querySelector<HTMLImageElement>('.mc-card-icon img');
        if (iconImg && server.favicon) {
            iconImg.src = server.favicon;
        }

        // Player section vs offline info
        updatePlayerSection(card, server);
    }

    // Reorder cards
    reorderCards(servers);

    // Summary stats
    updateSummary(servers);

    // Relative timestamps
    updateRelativeTimes();
}

// ---------------------------------------------------------------------------
// Latency
// ---------------------------------------------------------------------------

function updateLatency(card: HTMLElement, server: ServerData): void {
    let badge = card.querySelector<HTMLElement>('.mc-latency');

    if (server.isOnline && server.latency !== null) {
        if (!badge) {
            // Create badge if it doesn't exist (server just came online)
            const headerRow = card.querySelector('.d-flex.align-items-center.justify-content-between');
            if (headerRow) {
                badge = document.createElement('span');
                badge.className = 'badge mc-latency';
                headerRow.appendChild(badge);
            }
        }
        if (badge) {
            badge.textContent = `${Math.round(server.latency)}ms`;
            badge.classList.remove('mc-latency--good', 'mc-latency--ok', 'mc-latency--bad');
            if (server.latency < 50) {
                badge.classList.add('mc-latency--good');
            } else if (server.latency <= 150) {
                badge.classList.add('mc-latency--ok');
            } else {
                badge.classList.add('mc-latency--bad');
            }
            badge.style.display = '';
        }
    } else if (badge) {
        badge.style.display = 'none';
    }
}

// ---------------------------------------------------------------------------
// Player section
// ---------------------------------------------------------------------------

function updatePlayerSection(card: HTMLElement, server: ServerData): void {
    let offlineDiv = card.querySelector<HTMLElement>('.mc-offline-info');
    let rightCol = card.querySelector<HTMLElement>('.mc-card-right');
    const infoContainer = card.querySelector<HTMLElement>('.mc-card-info');

    if (server.isOnline) {
        // Remove offline info
        if (offlineDiv) offlineDiv.remove();

        // Player heads go in .mc-card-info (third line, aligned with "Last seen:")
        if (infoContainer) {
            diffPlayerHeads(infoContainer, server.players);
        }

        // Player count stays in .mc-card-right
        if (!rightCol) {
            rightCol = document.createElement('div');
            rightCol.className = 'mc-card-right flex-shrink-0';
            const cardBody = card.querySelector('.mc-card-body');
            if (cardBody) cardBody.appendChild(rightCol);
        }

        // Remove any stale heads from right col (migration from old layout)
        const oldHeads = rightCol.querySelector('.mc-player-heads');
        if (oldHeads) oldHeads.remove();

        let countEl = rightCol.querySelector<HTMLElement>('.mc-player-count');
        if (!countEl) {
            countEl = document.createElement('div');
            countEl.className = 'mc-player-count small text-muted';
            rightCol.prepend(countEl);
        }
        countEl.textContent = `${server.onlinePlayers}/${server.maxPlayers}`;
    } else {
        // Remove right column (count)
        if (rightCol) rightCol.remove();

        // Remove any player heads from info container
        if (infoContainer) {
            const heads = infoContainer.querySelector('.mc-player-heads');
            if (heads) heads.remove();
        }

        if (!offlineDiv) {
            offlineDiv = document.createElement('div');
            offlineDiv.className = 'mc-offline-info small text-muted';
            if (infoContainer) infoContainer.appendChild(offlineDiv);
        }

        if (server.lastSeenAt) {
            offlineDiv.innerHTML =
                `Last seen: <span class="mc-relative-time" data-time="${server.lastSeenAt}"></span>`;
        } else {
            offlineDiv.textContent = 'Never seen online';
        }
    }
}

// ---------------------------------------------------------------------------
// Player chip diffing
// ---------------------------------------------------------------------------

function diffPlayerHeads(container: HTMLElement, players: PlayerData[]): void {
    let headsDiv = container.querySelector<HTMLElement>('.mc-player-heads');

    if (players.length === 0) {
        if (headsDiv) headsDiv.remove();
        return;
    }

    if (!headsDiv) {
        headsDiv = document.createElement('div');
        headsDiv.className = 'mc-player-heads';
        container.appendChild(headsDiv);
    }

    const newUuids = new Set(players.map((p) => p.uuid));

    // Existing heads keyed by UUID
    const existingHeads = new Map<string, HTMLImageElement>();
    headsDiv.querySelectorAll<HTMLImageElement>('.mc-player-head').forEach((img) => {
        const uuid = img.dataset.uuid;
        if (uuid) {
            existingHeads.set(uuid, img);
        }
    });

    // Remove players no longer present
    for (const [uuid, img] of existingHeads) {
        if (!newUuids.has(uuid)) {
            img.remove();
        }
    }

    // Add new players
    for (const player of players) {
        if (!existingHeads.has(player.uuid)) {
            const img = document.createElement('img');
            img.src = `https://mc-heads.net/avatar/${player.uuid}/16`;
            img.loading = 'lazy';
            img.width = 16;
            img.height = 16;
            img.alt = player.name;
            img.title = player.name;
            img.className = 'mc-player-head rounded-sm';
            img.dataset.uuid = player.uuid;
            headsDiv.appendChild(img);
        }
    }
}

// ---------------------------------------------------------------------------
// Card reorder (online first, sorted by player count desc)
// ---------------------------------------------------------------------------

function reorderCards(servers: ServerData[]): void {
    // Build sort order: online first by player count desc, then offline
    const sorted = [...servers].sort((a, b) => {
        if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
        if (a.isOnline && b.isOnline) {
            return (b.onlinePlayers ?? 0) - (a.onlinePlayers ?? 0);
        }
        return 0;
    });

    sorted.forEach((server, i) => {
        const col = document.querySelector<HTMLElement>(
            `.mc-card-col[data-server-key="${server.key}"]`,
        );
        if (col) {
            col.style.order = String(i);
        }
    });
}

// ---------------------------------------------------------------------------
// Summary stats
// ---------------------------------------------------------------------------

function updateSummary(servers: ServerData[]): void {
    const el = document.getElementById('mc-summary');
    if (!el) return;

    let onlineCount = 0;
    let totalPlayers = 0;

    for (const s of servers) {
        if (s.isOnline) {
            onlineCount++;
            totalPlayers += s.onlinePlayers ?? 0;
        }
    }

    el.textContent = `${onlineCount}/${servers.length} online \u2014 ${totalPlayers} players`;
}

// ---------------------------------------------------------------------------
// Relative time
// ---------------------------------------------------------------------------

function updateRelativeTimes(): void {
    const now = Date.now();
    document.querySelectorAll<HTMLElement>('.mc-relative-time').forEach((el) => {
        const iso = el.dataset.time;
        if (!iso) return;

        const diff = Math.floor((now - new Date(iso).getTime()) / 1000);
        el.textContent = relativeTime(diff);
    });
}

function relativeTime(seconds: number): string {
    if (seconds < 0) return 'just now';
    if (seconds < 60) return 'just now';

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

    const months = Math.floor(days / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
}

// ---------------------------------------------------------------------------
// Detail page
// ---------------------------------------------------------------------------

let detailPollTimer: ReturnType<typeof setTimeout> | null = null;

// WR-01: keep the McConsole instance so destroy() can be called before
// re-init, removing the visibilitychange listener from the prior instance.
let mcConsoleInstance: McConsole | null = null;

function initDetail(container: HTMLElement): void {
    const statusUrl = container.dataset.statusUrl ?? '';

    initCopyButton(container);
    initBluemap(container);
    updateRelativeTimes();

    // Initialize console if section exists (auth-gated)
    const consoleSection = container.querySelector<HTMLElement>('.mc-console-section');
    if (consoleSection) {
        // WR-01: destroy prior instance before creating a new one to prevent
        // listener + socket leaks on SPA-style re-renders of the detail section.
        if (mcConsoleInstance !== null) {
            mcConsoleInstance.destroy();
        }
        mcConsoleInstance = new McConsole(consoleSection);
    }

    if (!statusUrl) return;

    // Start polling
    const scheduleDetailPoll = (): void => {
        detailPollTimer = setTimeout(() => pollDetail(container, statusUrl, scheduleDetailPoll), POLL_INTERVAL);
    };

    scheduleDetailPoll();

    // Visibility API: pause when hidden, immediate fetch on return
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            if (detailPollTimer !== null) {
                clearTimeout(detailPollTimer);
                detailPollTimer = null;
            }
        } else {
            pollDetail(container, statusUrl, scheduleDetailPoll);
        }
    });
}

async function pollDetail(
    container: HTMLElement,
    statusUrl: string,
    reschedule: () => void,
): Promise<void> {
    if (detailPollTimer !== null) {
        clearTimeout(detailPollTimer);
        detailPollTimer = null;
    }

    try {
        const resp = await fetch(statusUrl, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error(`[mc] Detail poll failed: ${resp.status}`);
        } else {
            const data: DetailServerData = await resp.json();
            updateDetail(container, data);
        }
    } catch (err) {
        console.error('[mc] Detail poll error:', err);
    }

    reschedule();
}

function updateDetail(container: HTMLElement, data: DetailServerData): void {
    // Status badge
    const badge = container.querySelector<HTMLElement>('h3 .badge');
    if (badge) {
        badge.classList.remove('bg-success', 'bg-danger');
        badge.classList.add(data.isOnline ? 'bg-success' : 'bg-danger');
        badge.textContent = data.isOnline ? 'Online' : 'Offline';
    }

    // MOTD
    const motdEl = container.querySelector<HTMLElement>('.mc-motd');
    if (motdEl) {
        if (data.motdHtml) {
            motdEl.innerHTML = data.motdHtml;
            motdEl.style.display = '';
        } else {
            motdEl.innerHTML = '';
            motdEl.style.display = 'none';
        }
    }

    // Version
    const versionEl = container.querySelector<HTMLElement>('.mc-detail-info .mb-2 strong');
    if (versionEl && versionEl.textContent === 'Version:') {
        const parent = versionEl.parentElement;
        if (parent) {
            parent.innerHTML = `<strong>Version:</strong> ${data.version ?? 'Unknown'}`;
        }
    }

    // Player count
    updateDetailPlayerCount(container, data);

    // Latency
    updateDetailLatency(container, data);

    // Favicon
    const iconImg = container.querySelector<HTMLImageElement>('.mc-detail-icon img');
    if (iconImg && data.favicon) {
        iconImg.src = data.favicon;
    }

    // Player list
    updateDetailPlayerList(container, data);

    // Last seen (offline)
    updateDetailLastSeen(container, data);

    // Connection address (defensive)
    const addressCode = container.querySelector<HTMLElement>('.mc-address code');
    if (addressCode && data.address) {
        addressCode.textContent = data.address;
    }
    const copyBtn = container.querySelector<HTMLElement>('.mc-copy-btn');
    if (copyBtn && data.address) {
        copyBtn.setAttribute('data-copy', data.address);
    }

    // Relative times
    updateRelativeTimes();
}

function updateDetailPlayerCount(container: HTMLElement, data: DetailServerData): void {
    // Find or create player count element
    const infoPanel = container.querySelector('.mc-detail-info');
    if (!infoPanel) return;

    let playerCountEl = infoPanel.querySelector<HTMLElement>('.mc-detail-player-count');

    if (data.isOnline) {
        if (!playerCountEl) {
            playerCountEl = document.createElement('div');
            playerCountEl.className = 'mb-2 mc-detail-player-count';
            // Insert after latency or version
            const latencyEl = infoPanel.querySelector('.mc-detail-latency');
            const ref = latencyEl ?? infoPanel.querySelector('.mc-address');
            if (ref) {
                ref.before(playerCountEl);
            } else {
                infoPanel.appendChild(playerCountEl);
            }
        }
        playerCountEl.innerHTML = `<strong>Players:</strong> ${data.onlinePlayers}/${data.maxPlayers}`;
    } else if (playerCountEl) {
        playerCountEl.remove();
    }
}

function updateDetailLatency(container: HTMLElement, data: DetailServerData): void {
    const infoPanel = container.querySelector('.mc-detail-info');
    if (!infoPanel) return;

    let latencyEl = infoPanel.querySelector<HTMLElement>('.mc-detail-latency');

    if (data.isOnline && data.latency !== null) {
        if (!latencyEl) {
            latencyEl = document.createElement('div');
            latencyEl.className = 'mb-2 mc-detail-latency';
            const addressEl = infoPanel.querySelector('.mc-address');
            if (addressEl) {
                addressEl.before(latencyEl);
            } else {
                infoPanel.appendChild(latencyEl);
            }
        }

        const cls = data.latency < 50 ? 'mc-latency--good'
            : data.latency <= 150 ? 'mc-latency--ok'
                : 'mc-latency--bad';
        latencyEl.innerHTML =
            `<strong>Latency:</strong> <span class="mc-latency ${cls}">${Math.round(data.latency)}ms</span>`;
    } else if (latencyEl) {
        latencyEl.remove();
    }
}

function updateDetailPlayerList(container: HTMLElement, data: DetailServerData): void {
    const playersSection = container.querySelector<HTMLElement>('.mc-detail-players');
    if (!playersSection) return;

    if (data.isOnline) {
        // Update header
        const header = playersSection.querySelector('h5');
        if (header) header.textContent = `Players (${data.onlinePlayers})`;

        // Remove "Server is offline" message if present
        const offlineMsg = playersSection.querySelector('p.text-muted');
        if (offlineMsg && offlineMsg.textContent === 'Server is offline') {
            offlineMsg.remove();
        }

        // Get or clear existing player rows
        const existingPlayers = playersSection.querySelectorAll('.mc-detail-player');
        existingPlayers.forEach((el) => el.remove());

        // Remove existing "No players online" message
        const noPlayersMsg = playersSection.querySelector('p.text-muted');
        if (noPlayersMsg) noPlayersMsg.remove();

        if (data.players.length > 0) {
            for (const player of data.players) {
                const row = document.createElement('div');
                row.className = 'mc-detail-player d-flex align-items-center gap-2 mb-2';
                row.innerHTML =
                    `<img src="https://mc-heads.net/avatar/${player.uuid}/32" ` +
                    `loading="lazy" width="32" height="32" ` +
                    `alt="${player.name}" class="mc-player-avatar rounded" ` +
                    `onerror="this.style.display='none'">` +
                    `<span>${player.name}</span>`;
                playersSection.appendChild(row);
            }
        } else {
            const msg = document.createElement('p');
            msg.className = 'text-muted';
            msg.textContent = 'No players online';
            playersSection.appendChild(msg);
        }
    } else {
        // Offline state
        const header = playersSection.querySelector('h5');
        if (header) header.textContent = 'Players';

        const existingPlayers = playersSection.querySelectorAll('.mc-detail-player');
        existingPlayers.forEach((el) => el.remove());

        const noPlayersMsg = playersSection.querySelector('p.text-muted');
        if (noPlayersMsg) {
            noPlayersMsg.textContent = 'Server is offline';
        } else {
            const msg = document.createElement('p');
            msg.className = 'text-muted';
            msg.textContent = 'Server is offline';
            playersSection.appendChild(msg);
        }
    }
}

function updateDetailLastSeen(container: HTMLElement, data: DetailServerData): void {
    const infoPanel = container.querySelector('.mc-detail-info');
    if (!infoPanel) return;

    let lastSeenEl = infoPanel.querySelector<HTMLElement>('.mc-detail-last-seen');

    if (!data.isOnline && data.lastSeenAt) {
        if (!lastSeenEl) {
            lastSeenEl = document.createElement('div');
            lastSeenEl.className = 'text-muted mb-2 mc-detail-last-seen';
            // Insert after the h3 heading
            const heading = infoPanel.querySelector('h3');
            if (heading) {
                heading.after(lastSeenEl);
            }
        }
        lastSeenEl.innerHTML =
            `Last seen: <span class="mc-relative-time" data-time="${data.lastSeenAt}"></span>`;
    } else if (lastSeenEl) {
        lastSeenEl.remove();
    }
}

// ---------------------------------------------------------------------------
// Clipboard copy
// ---------------------------------------------------------------------------

function initCopyButton(container: HTMLElement): void {
    const btn = container.querySelector<HTMLElement>('.mc-copy-btn');
    if (!btn) return;

    btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const address = btn.getAttribute('data-copy');
        if (!address) return;

        try {
            await navigator.clipboard.writeText(address);
            const originalTitle = btn.getAttribute('title') ?? '';
            btn.setAttribute('title', 'Copied!');
            btn.classList.add('mc-copied');

            setTimeout(() => {
                btn.setAttribute('title', originalTitle);
                btn.classList.remove('mc-copied');
            }, 1500);
        } catch {
            console.error('[mc] Clipboard write failed');
        }
    });
}

// ---------------------------------------------------------------------------
// Bluemap iframe load detection
// ---------------------------------------------------------------------------

function initBluemap(container: HTMLElement): void {
    const iframe = container.querySelector<HTMLIFrameElement>('.mc-bluemap-iframe');
    const shimmer = container.querySelector<HTMLElement>('.mc-bluemap-shimmer');
    const wrapper = container.querySelector<HTMLElement>('.mc-bluemap-wrapper');
    if (!iframe || !shimmer || !wrapper) return;

    // The URL lives in data-src (not src) so the browser does NOT fetch it on page load.
    // BlueMap is a heavy cross-origin WebGL app served over the homelab tunnel — we lazy-load
    // it only when the map section scrolls near the viewport, not on every detail-page view.
    const src = iframe.dataset.src;
    if (!src) return;

    let settled = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    const clearFallbackTimer = (): void => {
        if (fallbackTimer !== null) { clearTimeout(fallbackTimer); fallbackTimer = null; }
    };

    const reveal = (): void => {
        // Cross-origin: an HTTP error page (e.g. Cloudflare 502 when the server is stopped)
        // also fires 'load' and gets revealed — acceptable. A dead origin fires 'error'.
        if (settled) return;
        settled = true;
        clearFallbackTimer();
        shimmer.style.display = 'none';
        iframe.style.display = 'block';
    };

    const showFallback = (): void => {
        if (settled) return;
        settled = true;
        clearFallbackTimer();
        shimmer.style.display = 'none';
        wrapper.innerHTML = '<div class="text-muted text-center p-4">Map cannot be embedded — use "Open full map" link above</div>';
    };

    iframe.addEventListener('load', reveal);
    iframe.addEventListener('error', showFallback); // network-level failure (refused/DNS/blocked)

    // Drive laziness with an IntersectionObserver on the (always laid-out) wrapper rather
    // than the iframe's loading="lazy" attribute: the iframe starts display:none, and a
    // display:none element never satisfies lazy's intersection check — that combination
    // deadlocked (never loads → 'load' never fires → blind timer shows "cannot be embedded").
    const startLoad = (): void => {
        if (iframe.src) return; // already loading/loaded
        iframe.src = src;
        // Arm the stuck-connection safety net only AFTER load begins, so a not-yet-scrolled
        // map is never mis-flagged. reveal()/showFallback() clear it.
        fallbackTimer = setTimeout(showFallback, 8000);
    };

    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) {
                io.disconnect();
                startLoad();
            }
        }, { rootMargin: '200px' });
        io.observe(wrapper);
    } else {
        startLoad(); // no IntersectionObserver support → load immediately
    }
}
